import {
  collection,
  doc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { enqueueDeferredEvent } from './deferredEventOutbox'
import type { ContributionDayMetrics } from '../types/heatmap'

/** What kind of completion is being recorded — drives which counter bumps. */
export type ContributionKind = 'TASK' | 'CHECKLIST'

/**
 * Local-calendar `YYYY-MM-DD` for the supplied date. Uses the device's local
 * timezone (not UTC) so a 11 PM completion lands on today's cell, not
 * tomorrow's. The spec text mentioned IST, but per the live decision the
 * device's local TZ is authoritative.
 */
export const getLocalDateKey = (date: Date = new Date()): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const HEATMAP_HISTORY_WINDOW_DAYS = 365

/**
 * Append one completion event to today's `/users/{uid}/contribution_history/{dateKey}`
 * doc. Uses `setDoc({...}, { merge: true })` with `increment(1)` so:
 *   - First write of the day creates the doc with the counters.
 *   - Subsequent writes atomically bump the relevant counter without
 *     overwriting siblings or historical days.
 *
 * `dateKey` is also written as a field (not just the doc ID) so client-side
 * lookups can carry the value through filters/maps without re-deriving.
 */
export const recordContribution = async (
  uid: string,
  kind: ContributionKind,
): Promise<void> => {
  if (!uid) return
  const dateKey = getLocalDateKey()
  const queued = await enqueueDeferredEvent({
    actionKey: `CONTRIBUTION:${kind}`,
    kind: 'CONTRIBUTION',
    payload: { kind, dateKey },
  })
  if (queued) return
  const ref = doc(firestoreDb, 'users', uid, 'contribution_history', dateKey)
  try {
    await setDoc(
      ref,
      {
        dateKey,
        tasksCompletedCount: kind === 'TASK' ? increment(1) : increment(0),
        checklistItemsCompletedCount: kind === 'CHECKLIST' ? increment(1) : increment(0),
        totalActionsRecorded: increment(1),
      },
      { merge: true },
    )
  } catch (error) {
    // Fail open — heatmap is observational, not transactional. Don't bubble.
    console.warn('[heatmap] recordContribution failed', uid, kind, error)
  }
}

/** Normalize a Firestore contribution doc into our typed shape. */
const normalizeContribution = (id: string, data: Record<string, unknown>): ContributionDayMetrics => ({
  dateKey: typeof data.dateKey === 'string' ? data.dateKey : id,
  tasksCompletedCount: typeof data.tasksCompletedCount === 'number' ? data.tasksCompletedCount : 0,
  checklistItemsCompletedCount:
    typeof data.checklistItemsCompletedCount === 'number' ? data.checklistItemsCompletedCount : 0,
  totalActionsRecorded:
    typeof data.totalActionsRecorded === 'number' ? data.totalActionsRecorded : 0,
})

/**
 * Subscribe to the user's last 365 days of contribution history. Sorted desc
 * by doc-id (which is the dateKey, so lexicographic sort == chronological).
 *
 * Returns the unsubscribe fn — caller is responsible for cleanup.
 */
export const subscribeContributionHistory = (
  uid: string,
  onData: (history: Record<string, ContributionDayMetrics>) => void,
  onError?: (error: Error) => void,
): (() => void) => {
  const q = query(
    collection(firestoreDb, 'users', uid, 'contribution_history'),
    orderBy('__name__', 'desc'),
    limit(HEATMAP_HISTORY_WINDOW_DAYS),
  )
  return onSnapshot(
    q,
    (snapshot) => {
      const map: Record<string, ContributionDayMetrics> = {}
      for (const docSnap of snapshot.docs) {
        const metrics = normalizeContribution(docSnap.id, docSnap.data() as Record<string, unknown>)
        map[metrics.dateKey] = metrics
      }
      onData(map)
    },
    (error) => {
      console.warn('[heatmap] subscribeContributionHistory failed', uid, error)
      onError?.(error)
    },
  )
}
